import { useEffect, useRef, useState } from "react";
import { FiLoader, FiPaperclip, FiSmile, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";

const CHAT_EMOJIS = [
  "😀", "😂", "🥰", "😍", "😊", "😉", "😭", "😮", "😡", "👍", "👎", "🙏", "👏", "🔥", "💯", "❤️",
  "💙", "💚", "✨", "🎉", "✅", "❌", "⭐", "📎", "📷", "🎬", "☕", "🚀", "💡", "🤝", "💬", "📞",
];

export default function ConsultingChatComposer({
  isDark,
  canChat,
  sending,
  uploadingFile,
  input,
  setInput,
  onSubmit,
  onAttach,
  replyTo,
  onCancelReply,
}) {
  const { t } = useTranslation();
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!emojiOpen) return undefined;
    const onDoc = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [emojiOpen]);

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) {
      setInput((prev) => `${prev}${emoji}`);
      return;
    }
    const start = el.selectionStart ?? input.length;
    const end = el.selectionEnd ?? input.length;
    const next = input.slice(0, start) + emoji + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const border = "border-separator-default";
  const muted = "text-label-secondary";

  return (
    <div className={`border-t ${border}`}>
      {replyTo && (
        <div
          className={`flex items-start gap-2 border-b px-3 py-2 text-footnote ${border} ${
            "bg-fill-tertiary"
          }`}
        >
          <div className="min-w-0 flex-1 border-l-2 border-app-consulting-base pl-2">
            <p className={`font-semibold ${isDark ? "text-app-consulting-soft" : "text-app-consulting-soft"}`}>
              {t("consulting.replyingTo", { name: replyTo.senderName || "" }) ||
                `Reply to ${replyTo.senderName || ""}`}
            </p>
            <p className={`truncate ${muted}`}>{replyTo.text}</p>
          </div>
          <button
            type="button"
            onClick={onCancelReply}
            className={`rounded-radius-xs p-1 hover:bg-fill-quaternary`}
            aria-label={t("common.cancel") || "Cancel"}
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
      )}
      <form onSubmit={onSubmit} className="flex gap-2 p-2">
        <button
          type="button"
          disabled={!canChat || uploadingFile}
          onClick={onAttach}
          className={`rounded-radius-sm border p-2 ${border} disabled:opacity-50`}
          aria-label={t("consulting.attach") || "Attach file"}
        >
          {uploadingFile ? (
            <FiLoader className="h-4 w-4 animate-spin" />
          ) : (
            <FiPaperclip className="h-4 w-4" />
          )}
        </button>
        <div className="relative" ref={emojiRef}>
          <button
            type="button"
            disabled={!canChat}
            onClick={() => setEmojiOpen((o) => !o)}
            className={`rounded-radius-sm border p-2 ${border} disabled:opacity-50`}
            aria-label={t("consulting.addEmoji") || "Emoji"}
          >
            <FiSmile className="h-4 w-4" />
          </button>
          {emojiOpen && (
            <div
              className={`absolute bottom-full left-0 z-20 mb-1 grid w-[min(16rem,70vw)] grid-cols-8 gap-0.5 rounded-radius-sm border p-2 shadow-xl ${
                "border-separator-default bg-bg-raised"
              }`}
            >
              {CHAT_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className={`rounded-md p-1 text-lg leading-none ${
                    "hover:bg-fill-quaternary"
                  }`}
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={!canChat}
          placeholder={
            canChat
              ? replyTo
                ? t("consulting.replyPlaceholder") || "Write a reply…"
                : t("consulting.messagePlaceholder") || "Write a message…"
              : t("consulting.accessEnded") || "Access ended"
          }
          className={`min-w-0 flex-1 rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${
            isDark
              ? "border-white/10 bg-white/5 text-slate-100 placeholder-slate-500"
              : "border-slate-200 bg-white"
          }`}
        />
        <button
          type="submit"
          disabled={sending || !canChat}
          className="rounded-radius-sm bg-app-consulting-base px-3 py-2 text-footnote-semibold text-app-consulting-on disabled:opacity-50"
        >
          {t("consulting.send") || "Send"}
        </button>
      </form>
    </div>
  );
}
